/**
 * InviteCoParentSheet — Plan 10.
 *
 * Live (no-longer-stub) flow for "Invite a co-owner" on the /my-family
 * page. Calls `POST /api/family/accounts/:accountId/co-owners/invite`
 * which mints a QUEUED Invitation; Plan 36's dispatcher handles the
 * actual delivery.
 *
 * Supersedes the earlier `AddParentSheet` toast-only stub. This is the
 * canonical Plan 10 invite surface, and it IS live — mounted by
 * `<HouseholdMembersCard />`, the "Guardians" card on /my-family.
 *
 * Adult invites carry the Plan 36 triple (email + name + phone). All
 * three are required; the server enforces the same on the manager
 * boundary, but client-side validation gives instant feedback.
 */

import { FormField } from '@ordo/ui/form-field'
import { FormSheet } from '@ordo/ui/form-sheet'
import { Input } from '@ordo/ui/input'
import { Label } from '@ordo/ui/label'
import { RadioGroup, RadioGroupItem } from '@ordo/ui/radio-group'
import { toast } from '@ordo/ui/sonner'
import { Check } from 'lucide-react'
import { useState } from 'react'

import {
  type HouseholdRole,
  type InviteCoOwnerInput,
  type ListCoOwnersResponse,
  useInviteAccountCoOwnerMutation,
} from '@/api/co-owners'
import { type ApiError } from '@/api/http'

import { isPlausibleEmail, isPlausiblePhone } from './helpers'
import { HOUSEHOLD_PERMISSION_LABEL, HOUSEHOLD_ROLE_DESCRIPTION, HOUSEHOLD_ROLE_LABEL } from './household-roles'

export interface InviteCoParentSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Account that's being added to. */
  accountId: string
  /**
   * Role -> permissions from the server. Empty for ORGANIZATION accounts, which
   * don't use household roles — the picker hides itself in that case.
   */
  roleCatalog?: ListCoOwnersResponse['roleCatalog']
  /** Whether the inviter may grant primary-guardian status (MANAGE_PRIMARY_GUARDIAN). */
  canAssignOwner?: boolean
  /** Optional callback fired after a successful invite. */
  onInvited?: () => void
}

interface FormState {
  firstName: string
  lastName: string
  email: string
  phone: string
}

const INITIAL: FormState = { firstName: '', lastName: '', email: '', phone: '' }

export function InviteCoParentSheet({
  open,
  onOpenChange,
  accountId,
  roleCatalog = [],
  canAssignOwner = false,
  onInvited,
}: InviteCoParentSheetProps) {
  const [form, setForm] = useState<FormState>(INITIAL)
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({})
  // Guardian is the default: the common case is a second parent with full
  // rights short of changing who the primary guardian is.
  const [role, setRole] = useState<HouseholdRole>('GUARDIAN')
  const inviteMutation = useInviteAccountCoOwnerMutation(accountId)
  const selectableRoles = roleCatalog.filter((entry) => entry.role !== 'PRIMARY_GUARDIAN' || canAssignOwner)

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => {
      if (!prev[key]) {
        return prev
      }
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  function validate(): boolean {
    const next: Partial<Record<keyof FormState, string>> = {}
    if (!form.firstName.trim()) {
      next.firstName = 'Required'
    }
    if (!form.lastName.trim()) {
      next.lastName = 'Required'
    }
    if (!isPlausibleEmail(form.email)) {
      next.email = 'Enter a valid email so we can deliver the magic link.'
    }
    if (!isPlausiblePhone(form.phone)) {
      next.phone = 'A cell phone is required so we can text invite + alerts.'
    }
    setErrors(next)
    return Object.keys(next).length === 0
  }

  async function handleSubmit() {
    if (!validate()) {
      throw new Error('Fix the highlighted fields and try again.')
    }
    const fullName = `${form.firstName.trim()} ${form.lastName.trim()}`.trim()
    const payload: InviteCoOwnerInput = {
      email: form.email.trim(),
      name: fullName,
      phone: form.phone.trim(),
      ...(selectableRoles.length > 0 ? { role } : {}),
    }
    try {
      await inviteMutation.mutateAsync(payload)
      toast.success('Invite sent', {
        description:
          selectableRoles.length > 0
            ? `${fullName} will get a magic-link email and join as ${HOUSEHOLD_ROLE_LABEL[role].toLowerCase()}.`
            : `${fullName} will receive a magic-link email to join this household.`,
      })
      setForm(INITIAL)
      setErrors({})
      setRole('GUARDIAN')
      onInvited?.()
    } catch (err) {
      const message = (err as ApiError | Error).message ?? 'Failed to send invite'
      // Re-throw so FormSheet surfaces it inline; toast for visibility.
      toast.error("Couldn't send invite", { description: message })
      throw new Error(message, { cause: err })
    }
  }

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Add a member"
      description="Add another adult to this household — they'll get a magic-link invite, with the role you pick below."
      submitLabel="Send invite"
      onSubmit={handleSubmit}
      onReset={() => {
        setForm(INITIAL)
        setErrors({})
        setRole('GUARDIAN')
      }}
      size="md">
      <FormField label="First name" htmlFor="invite-co-parent-first" required error={errors.firstName}>
        <Input
          id="invite-co-parent-first"
          value={form.firstName}
          onChange={(e) => update('firstName', e.target.value)}
          autoComplete="given-name"
          placeholder="Riley"
        />
      </FormField>
      <FormField label="Last name" htmlFor="invite-co-parent-last" required error={errors.lastName}>
        <Input
          id="invite-co-parent-last"
          value={form.lastName}
          onChange={(e) => update('lastName', e.target.value)}
          autoComplete="family-name"
          placeholder="Patel"
        />
      </FormField>
      <FormField
        label="Email"
        htmlFor="invite-co-parent-email"
        required
        error={errors.email}
        helperText="We send the invitation as a magic link to this address.">
        <Input
          id="invite-co-parent-email"
          type="email"
          value={form.email}
          onChange={(e) => update('email', e.target.value)}
          autoComplete="email"
          placeholder="riley@example.com"
        />
      </FormField>
      <FormField
        label="Cell phone"
        htmlFor="invite-co-parent-phone"
        required
        error={errors.phone}
        helperText="Required for adult co-owners — we text about meal cutoffs and unserved-meal flags.">
        <Input
          id="invite-co-parent-phone"
          type="tel"
          value={form.phone}
          onChange={(e) => update('phone', e.target.value)}
          autoComplete="tel"
          placeholder="+1 (555) 123-4567"
        />
      </FormField>
      {selectableRoles.length > 0 ? (
        <FormField label="Role" htmlFor={`invite-role-${selectableRoles[0]?.role}`} required>
          <RadioGroup
            value={role}
            onValueChange={(value) => setRole(value as HouseholdRole)}
            className="flex flex-col gap-3">
            {selectableRoles.map((entry) => (
              <Label
                key={entry.role}
                htmlFor={`invite-role-${entry.role}`}
                className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
                <RadioGroupItem id={`invite-role-${entry.role}`} value={entry.role} className="mt-0.5" />
                <span className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <span className="text-sm font-medium text-foreground">{HOUSEHOLD_ROLE_LABEL[entry.role]}</span>
                  <span className="text-xs text-muted-foreground">{HOUSEHOLD_ROLE_DESCRIPTION[entry.role]}</span>
                  <span className="flex flex-col gap-1">
                    {entry.permissions.map((permission) => (
                      <span key={permission} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
                        {HOUSEHOLD_PERMISSION_LABEL[permission]}
                      </span>
                    ))}
                  </span>
                </span>
              </Label>
            ))}
          </RadioGroup>
        </FormField>
      ) : (
        <div className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
          <p className="font-medium text-foreground">Co-owners can:</p>
          <ul className="mt-1 list-disc space-y-0.5 pl-4">
            <li>Place orders for any student on the account.</li>
            <li>Edit dietary preferences + school info.</li>
            <li>Manage their own sign-in credentials.</li>
          </ul>
          <p className="mt-2">
            They cannot reassign ownership. Use &quot;Transfer ownership&quot; from the management card if you want a
            co-owner to become the account owner.
          </p>
        </div>
      )}
    </FormSheet>
  )
}
